import { Typography } from '@/components/ui/typography';
import { HeroCarousel } from '../../property-shared/HeroCarousel';
import { SyncManagedBanner } from './SyncManagedBanner';

import type { HeroSource } from '../../property-view/property-detail-drawer/types';
import type { PropertyEntity } from '../../../../types/property.types';

interface Props {
  property: PropertyEntity;
  /** Forwarded directly to HeroCarousel as its `source` prop. */
  heroSource: HeroSource;
  isSyncManaged: boolean;
}

export function PropertyHeroCard({ property, heroSource, isSyncManaged }: Props) {
  const addressLine2 = property.city && property.state && property.zipCode
    ? `${property.city} ${property.state}, ${property.zipCode}`
    : [property.city, property.state, property.zipCode].filter(Boolean).join(' ');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,400px)_1fr] gap-6 rounded-lg border border-border bg-card p-6">
      {/* Left: photoset carousel */}
      <div className="lg:max-w-[400px]">
        <HeroCarousel
          key={property.propId}
          source={heroSource}
          leasingStatus={property.leaseStatus}
          ariaLabel={property.propertyAddress ?? property.address1}
        />
      </div>

      {/* Right: summary */}
      <div className="flex flex-col gap-4">
        <div className="space-y-1">
          <Typography as="h1" variant="heading-lg" className="font-semibold">
            {property.propertyName ?? property.address1}
          </Typography>
          <div className="text-muted-foreground text-sm">
            {addressLine2}
          </div>
        </div>

        <PropertyMeta property={property} />

        {isSyncManaged && (
          <SyncManagedBanner
            pmsSource={property.pmsSource}
            lastSynced={property.updatedAt}
          />
        )}
      </div>
    </div>
  );
}

const PropertyMeta = ({ property }: { property: PropertyEntity }) => {
  const rows: Array<{ label: string; value: string | null | undefined }> = [
    { label: 'Entity', value: property.entity },
    { label: 'Property ID', value: property.clientPropertyId },
    { label: 'Unit Status', value: property.propertyStatus },
    { label: 'Portfolio Name', value: property.portfolioName },
  ];
  return (
    <dl className="space-y-1 text-sm">
      {rows.map(({ label, value }) => (
        <div key={label} className="flex gap-2">
          <dt className="text-muted-foreground">{label}:</dt>
          <dd className="text-foreground">{value && String(value).trim() !== '' ? value : '—'}</dd>
        </div>
      ))}
    </dl>
  );
};
